import Tabs from "@/components/common/Tabs";
import { useHistoryStore } from "@/modules/auth/store/history";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Transaction from "./Transaction";
import { Icon } from "@/components/common/icon/Icon";

const historyKeyToTypeMap = {
  all: "",
  deposits: "dep",
  rewards: "dep-reward",
  airdrop: "dep-ref-reward",
} as const;

const Transactions = () => {
  const { t } = useTranslation();

  const { all, deposits, rewards, airdrop, isLoading, hasMoreMap, setHistory } =
    useHistoryStore();

  type TabKey = "all" | "deposits" | "rewards" | "airdrop";
  const [activeTab, setActiveTab] = useState<TabKey>("all");

  const tabs = [
    { value: "all", label: t("history-page.filters.all") },
    { value: "deposits", label: t("history-page.filters.deposits") },
    { value: "rewards", label: t("history-page.filters.rewards") },
    { value: "airdrop", label: t("history-page.filters.airdrop") },
  ];

  const loaderRef = useRef<HTMLDivElement | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  const currentTransactions = useMemo(() => {
    if (activeTab === "all") return all;
    if (activeTab === "deposits") return deposits;
    if (activeTab === "rewards") return rewards;
    if (activeTab === "airdrop") return airdrop;

    return [];
  }, [activeTab, all, deposits, rewards, airdrop]);

  useEffect(() => {
    if (!loaderRef.current || isLoading) return;

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (
        entries[0].isIntersecting &&
        hasMoreMap[historyKeyToTypeMap[activeTab]] !== false
      ) {
        setHistory({
          type: !historyKeyToTypeMap[activeTab]
            ? undefined
            : historyKeyToTypeMap[activeTab],
          offset: currentTransactions.length || 0,
        });
      }
    });

    observer.current.observe(loaderRef.current);

    return () => observer.current?.disconnect();
  }, [
    loaderRef,
    isLoading,
    activeTab,
    hasMoreMap,
    currentTransactions.length,
    setHistory,
  ]);

  return (
    <>
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={(val) => setActiveTab(val as TabKey)}
      />

      <div className="w-full flex flex-col bg-bg rounded-[0.75rem]">
        <p className="text-[0.8125rem] leading-[1rem] text-section-header-text uppercase p-4 pb-2">
          {t("history-page.title")}
        </p>

        {currentTransactions.map((tx, index) => (
          <Fragment key={index}>
            <Transaction {...tx} />

            {index !== currentTransactions.length - 1 && (
              <div className="w-full pl-18">
                <div className="w-full h-px bg-divider"></div>
              </div>
            )}
          </Fragment>
        ))}

        {currentTransactions.length === 0 && !isLoading && (
          <p className="text-[1.0625rem] leading-[1.375rem] text-hint text-center p-4">
            {t("history-page.no_transactions")}
          </p>
        )}
      </div>

      {isLoading && (
        <div className="w-full flex items-center justify-center py-4">
          <Icon type="loading" className="w-10 h-10 fill-link animate-spin" />
        </div>
      )}

      {(hasMoreMap[activeTab] ?? true) && (
        <div ref={loaderRef} className="w-full h-px"></div>
      )}
    </>
  );
};

export default Transactions;
